import { PropsWithChildren, createContext, useEffect, useState } from "react";
import { createObjectData, createTableAndChartData } from "../utils/processing";
import * as columns from "../utils/columnConstants";
import type { Scope } from "../utils/scopes";

export type Summary = {
    columns: string[];
    data: {
        title: string;
        values: (number | undefined)[];
    }[];
    columnTotals: (number | undefined)[];
};

export type SummaryWithTotals = {
    columns: string[];
    data: {
        title: string;
        values: (number | undefined)[];
        total: number | undefined;
    }[];
    columnTotals: (number | undefined)[];
    total: number | undefined;
};
export type InvestmentDataType = {
    clients: number | undefined;
    countries: number | undefined;
    totalRevenue: number | undefined;
    totalOutstanding: number | undefined;
};

export type ExcelJsonRow = {
    [columns.Indicator]: string;
    [columns.SubIndicator]: string;
    [columns.Scope]: string;
    [columns.SubScope]: string;
    [columns.LDC]: string;
    [columns.Income]: string;
    [columns.Region]: string;
    [columns.SubRegion]: string;
    [columns.Country]: string;
    [columns.EconAct]: string;
    [columns.EconActCode]: string;
    [columns.Client]: string;
    [columns.ClientType]: string;
    [columns.Investee]: string;
    [columns.Comment]: string;

    [columns.ReportingYear]: number | undefined;
    [columns.FiscalYear]: number | undefined;
    [columns.ProjectTimeline]: number | undefined;
    [columns.Revenue]: number | undefined;
    [columns.OutstandingAmount]: number | undefined;
    [columns.PowerProduction]: number | undefined;
    [columns.AttributedTotal]: number | undefined;
    [columns.Total]: number | undefined;
    [columns.YouthEmployment]: number | undefined;
    [columns.ImpactOutstanding]: number | undefined;
    [columns.ImpactRevenue]: number | undefined;

    [columns.SectorAgriculture]: number | undefined;
    [columns.SectorMining]: number | undefined;
    [columns.SectorManufacturing]: number | undefined;
    [columns.SectorUtilities]: number | undefined;
    [columns.SectorConstruction]: number | undefined;
    [columns.SectorVehicles]: number | undefined;
    [columns.SectorAccomodation]: number | undefined;
    [columns.SectorTransport]: number | undefined;
    [columns.SectorFinancial]: number | undefined;
    [columns.SectorBusiness]: number | undefined;
    [columns.SectorOther]: number | undefined;
    [columns.SectorPublic]: number | undefined;
    [columns.SectorEducation]: number | undefined;
    [columns.SectorSocial]: number | undefined;

    [columns.AttributionOutstanding]: number | undefined;
    [columns.AttributionCommitted]: number | undefined;
    [columns.AttributionShareCommitted]: number | undefined;
    [columns.AttributionShareOutstanding]: number | undefined;
};
export type ExcelJsonSheet = Array<ExcelJsonRow>;

export type ObjectData = {
    employment: EmploymentObjectData[];
    general: GeneralObjectData[];
    ghg: GhgObjectData[];
    valueAdded: ValueAddedObjectData[];
    attribution: AttributionObjectData[];
};

export type GeneralObjectData = {
    id: string;
    client: string;
    country: string;
    econAct: string;
    investee: string | undefined;
    scope: Scope;
    year: number | undefined;
    revenue: number | undefined;
    outstandingAmount: number | undefined;
    outstandingShare: number | undefined;
    committedShare: number | undefined;
};

export type GhgObjectData = {
    id: string;
    client: string;
    country: string;
    econAct: string;
    investee: string | undefined;
    scope: Scope;
    year: number | undefined;
    outstandingAmount: number | undefined;
    outstandingShare: number | undefined;
    committedShare: number | undefined;
    scope1: { co2: number | undefined; nonco2: number | undefined; total: number | undefined };
    scope2: { co2: number | undefined; nonco2: number | undefined; total: number | undefined };
    scope3: { co2: number | undefined; nonco2: number | undefined; total: number | undefined };
    scope3_downstream: { co2: number | undefined; nonco2: number | undefined; total: number | undefined };
};

export type EmploymentObjectData = {
    id: string;
    client: string;
    country: string;
    econAct: string;
    investee: string | undefined;
    scope: Scope;
    year: number | undefined;
    outstandingAmount: number | undefined;
    outstandingShare: number | undefined;
    committedShare: number | undefined;
    direct: { total: number | undefined; female: number | undefined; formal: number | undefined };
    supply: { total: number | undefined; female: number | undefined; formal: number | undefined };
    induced: { total: number | undefined; female: number | undefined; formal: number | undefined };
    power: { total: number | undefined; female: number | undefined; formal: number | undefined };
    combined: { total: number | undefined; female: number | undefined; formal: number | undefined };
};

export type ValueAddedObjectData = {
    id: string;
    client: string;
    country: string;
    econAct: string;
    investee: string | undefined;
    scope: Scope;
    year: number | undefined;
    outstandingAmount: number | undefined;
    outstandingShare: number | undefined;
    committedShare: number | undefined;
    // no induced here
    direct: { netIncome: number | undefined; wages: number | undefined; taxes: number | undefined };
    supply: { netIncome: number | undefined; wages: number | undefined; taxes: number | undefined };
    power: { netIncome: number | undefined; wages: number | undefined; taxes: number | undefined };
    combined: { netIncome: number | undefined; wages: number | undefined; taxes: number | undefined };
};

export type AttributionObjectData = {
    id: string;
    client: string;
    country: string;
    econAct: string;
    year: number | undefined;
    outstandingAmount: number | undefined;
    outstandingShare: number | undefined;
    committedShare: number | undefined;
};

export type OverviewObjectData = {
    client: string;
    country: string;
    econAct: string;
    investee: string | undefined;
    outstandingAmount: number | undefined;
    ghg: GhgObjectData | undefined;
    employment: EmploymentObjectData | undefined;
    valueAdded: ValueAddedObjectData | undefined;
};

export type RawData = {
    employment: ExcelJsonSheet;
    ghg: ExcelJsonSheet;
    valueAdded: ExcelJsonSheet;
    general: ExcelJsonSheet;
    attribution: ExcelJsonSheet;
};

export type TableData = {
    ghg: SingleTableData;
    employment: SingleTableData;
    valueAdded: SingleTableData;
    overview: SingleTableData;
};

export type SingleTableData = {
    columns: any; //TODO improve typing here?
    data: (string | number | undefined)[][];
};

// TODO: Better name?
export type Data = {
    investmentData: InvestmentDataType;

    employmentSummary: Summary;
    ghgSummary: Summary;
    valueAddedSummary: Summary;

    ghgIndividualClientsChartData: SummaryWithTotals;
    ghgSectoralExposureChartData: SummaryWithTotals;

    employmentIndividualClientsTableData: SummaryWithTotals;
    employmentIndividualClientsChartData: SummaryWithTotals;
    employmentSectoralExposureTableData: SummaryWithTotals;
    employmentSectoralExposureChartData: SummaryWithTotals;

    valueAddedIndividualClientsChartData: SummaryWithTotals;
    valueAddedSectoralExposureChartData: SummaryWithTotals;

    ghgTableData: SingleTableData;
    employmentTableData: SingleTableData;
    valueAddedTableData: SingleTableData;
    overviewTableData: SingleTableData;
};

export const exampleData: Data = {
    investmentData: { clients: 10, countries: 15, totalRevenue: 40, totalOutstanding: 25 },
    employmentSummary: {
        columns: ["employmentTotal", "employmentIntensity"],
        data: [
            { title: "direct", values: [17965, 2000000] },
            { title: "induced", values: [17965, 2000000] },
            { title: "supplyChain", values: [7366, 2000000] },
            { title: "financeEnabling", values: [38921, 2000000] },
            { title: "powerEnabling", values: [7366, 2000000] },
        ],
        columnTotals: [64251, 10000000],
    },
    ghgSummary: {
        columns: ["emissionTotal", "emissionIntensity"],
        data: [
            { title: "scope1", values: [17965, 2000000] },
            { title: "scope2", values: [17965, 2000000] },
            { title: "scope3 upstream", values: [7366, 2000000] },
            { title: "scope3 downstream", values: [7366, 2000000] },
        ],
        columnTotals: [44000, 6000000],
    },
    valueAddedSummary: {
        columns: ["economicTotal", "economicIntensity"],
        data: [
            { title: "wages", values: [12899, 2000000] },
            { title: "netIncome", values: [17108, 2000000] },
            { title: "taxes", values: [3666, 2000000] },
        ],
        columnTotals: [33673, 6000000],
    },
    ghgIndividualClientsChartData: {
        columns: ["co2", "nonCo2"],
        data: [
            { title: "scope1", values: [623, 6327], total: 6950 },
            { title: "scope2", values: [3721, 18], total: 3739 },
            { title: "scope3 upstream", values: [6707, 14611], total: 21318 },
            { title: "scope3 downstream", values: [6707, 14611], total: 21318 },
        ],
        columnTotals: [11051, 20956],
        total: 32007,
    },
    ghgSectoralExposureChartData: {
        columns: ["co2", "nonCo2"],
        data: [
            { title: "scope1", values: [623, 6327], total: 6950 },
            { title: "scope2", values: [3721, 18], total: 3739 },
            { title: "scope3 upstream", values: [6707, 14611], total: 21318 },
            { title: "scope3 downstream", values: [6707, 14611], total: 21318 },
        ],
        columnTotals: [11051.555, 20956.555],
        total: 32007.555,
    },
    employmentIndividualClientsTableData: {
        columns: ["ofWhichFemale", "ofWhichFormal"],
        data: [
            { title: "direct", values: [0, 541], total: 541 },
            { title: "induced", values: [125, 588], total: 588 },
            { title: "supplyChain", values: [344, 1114], total: 1114 },
            { title: "financeEnabling", values: [867, 11285], total: 11285 },
            { title: "powerEnabling", values: [867, 11285], total: 11285 },
        ],
        columnTotals: [13036, 13528],
        total: 13528,
    },
    employmentIndividualClientsChartData: {
        columns: ["clients"],
        data: [
            { title: "direct", values: [541], total: 541 },
            { title: "induced", values: [588], total: 588 },
            { title: "supplyChain", values: [1114], total: 1114 },
            { title: "financeEnabling", values: [11285], total: 11285 },
            { title: "powerEnabling", values: [11285], total: 11285 },
        ],
        columnTotals: [24813],
        total: 24813,
    },
    employmentSectoralExposureTableData: {
        columns: ["ofWhichFemale", "ofWhichFormal"],
        data: [
            { title: "direct", values: [0, 917], total: 917 },
            { title: "induced", values: [125, 1064], total: 1064 },
            { title: "supplyChain", values: [222, 1180], total: 1180 },
        ],
        columnTotals: [1392, 3161],
        total: 3161,
    },
    employmentSectoralExposureChartData: {
        columns: ["clients", "total"],
        data: [
            { title: "direct", values: [917], total: 917 },
            { title: "induced", values: [1064], total: 1064 },
            { title: "supplyChain", values: [1180], total: 1180 },
        ],
        columnTotals: [3161],
        total: 3161,
    },
    valueAddedIndividualClientsChartData: {
        columns: ["wages", "netIncome", "taxes"],
        data: [
            { title: "direct", values: [2417, 8660, 1576], total: 12652 },
            { title: "supplyChain", values: [5975, 10307, 562], total: 16844 },
        ],
        columnTotals: [10091, 22670, 3099],
        total: 35860,
    },
    valueAddedSectoralExposureChartData: {
        columns: ["wages", "netIncome", "taxes"],
        data: [
            { title: "direct", values: [8060, 10516, 2051], total: 20627 },
            { title: "supplyChain", values: [4839, 6592, 1615], total: 13046 },
        ],
        columnTotals: [12699, 17106, 3666],
        total: 33673,
    },

    valueAddedTableData: {
        columns: [
            { filter: true, columns: ["client", "country", "activity", "totalOutstanding"] },
            {
                color: "valueAdded",
                header: "direct",
                columns: ["netIncome", "taxes", "wages", "totalContribution"],
            },
            {
                color: "valueAdded",
                header: "supplyChain",
                columns: ["netIncome", "taxes", "wages", "totalContribution"],
            },

            {
                color: "valueAdded",
                connected: true,
                columns: ["economicIntensity"],
            },
        ],
        data: [
            [
                "Accounting, accounting and auditing activities",
                "egypt",
                "econAct1",
                2222222,
                2222222,
                2222222,
                2222222,
                2222222,
                2222222,
                2222222,
                2222222,
                2222222,
                2222222,
            ],
            [
                "Accounting, accounting and auditing activities",
                "gambia",
                "econAct1",
                1111111,
                1111111,
                1111111,
                1111111,
                1111111,
                1111111,
                1111111,
                1111111,
                1111111,
                1111111,
            ],
            [
                "Accounting, accounting and auditing activities",
                "egypt",
                "econAct2",
                3333333,
                3333333,
                3333333,
                3333333,
                3333333,
                3333333,
                3333333,
                3333333,
                3333333,
                3333333,
            ],
        ],
    },
    ghgTableData: {
        columns: [
            { filter: true, columns: ["client", "country", "activity", "totalOutstanding"] },
            {
                color: "emission",
                header: "direct",
                columns: ["scope1emissions"],
            },
            {
                color: "emission",
                header: "supplyChain",
                columns: ["scope2emissions", "scope3emissions", "scope3downstreamEmissions"],
            },

            {
                color: "emission",
                connected: true,
                columns: ["scope1&2", "scope1&2&3", "scope1&2(mUSD)", "scope1&2&3(mUSD)"],
            },
        ],
        data: [
            ["Accounting, accounting and auditing activities", "egypt", "econAct1", 2222222, 2222222, 2222222, 2222222, 2222222, 2222222, 2222222, 2222222, 2222222],
            ["Accounting, accounting and auditing activities", "gambia", "econAct1", 1111111, 1111111, 1111111, 1111111, 1111111, 1111111, 1111111, 1111111, 1111111],
            ["Accounting, accounting and auditing activities", "egypt", "econAct2", 3333333, 3333333, 3333333, 3333333, 3333333, 3333333, 3333333, 3333333, 3333333],
        ],
    },
    employmentTableData: {
        columns: [
            { filter: true, columns: ["client", "country", "activity", "totalOutstanding"] },
            {
                color: "employment",
                header: "direct",
                columns: ["total"],
            },
            {
                color: "employment",
                header: "supplyChain",
                columns: ["total"],
            },
            {
                color: "employment",
                header: "induced",
                columns: ["total"],
            },
            {
                color: "employment",
                header: "powerEnabling",
                columns: ["total"],
            },
            {
                color: "employment",
                header: "finance",
                columns: ["total"],
            },

            {
                color: "employment",
                connected: true,
                columns: ["employmentIntensity"],
            },
        ],
        data: [
            ["Accounting, accounting and auditing activities", "egypt", "econAct1", 2222222, 2222222, 2222222, 2222222, 2222222, -1, 2222222],
            ["Accounting, accounting and auditing activities", "gambia", "econAct1", 1111111, 1111111, 1111111, 1111111, 1111111, -1, 1111111],
            ["Accounting, accounting and auditing activities", "egypt", "econAct2", 3333333, 3333333, 3333333, 3333333, 3333333, -1, 3333333],
        ],
    },
    overviewTableData: {
        columns: [
            { filter: true, columns: ["client", "country", "activity", "totalOutstanding"] },
            {
                color: "emission",
                header: "ghgEmissions",
                columns: ["scope1&2", "scope1&2&3", "scope1&2(mUSD)"],
            },
            {
                color: "employment",
                header: "employment",
                columns: ["totalEmployment", "employmentIntensity"],
            },

            {
                color: "valueAdded",
                header: "valueAdded",
                columns: ["totalContribution", "economicIntensity"],
            },
        ],
        data: [
            ["Accounting, accounting and auditing activities", "egypt", "econAct1", 2222222, 2222222, 2222222, 2222222, 2222222, 2222222, 2222222, 2222222],
            ["Accounting, accounting and auditing activities", "gambia", "econAct1", 1111111, 1111111, 1111111, 1111111, 1111111, 1111111, 1111111, 1111111],
            ["Accounting, accounting and auditing activities", "egypt", "econAct2", 3333333, 3333333, 3333333, 3333333, 3333333, 3333333, 3333333, 3333333],
        ],
    },
};

// "Committed" was removed from the dashboard selector (Ceren/FH/Rebekah 2026-09-18): only
// "No filter" and "Outstanding" remain selectable. The underlying committed-share data/columns
// are untouched (still parsed, still in the Excel output) -- this only drops the dashboard's
// ability to select it as the multiplier.
export type AttributionFilterOption = "noFilter" | "outstanding";

export type Filter = {
    years: number[] | null;
    attribution: AttributionFilterOption | null;
};

const exampleContext = {
    showExampleData: () => console.log("processing"),

    data: null,
    filter: {},
    rawDataReceived: () => {},
    yearsFilterOptions: [2022, 2023, 2024, 2025],
    yearsFilter: [],
    setYearsFilter: (y: number[]) => console.log(`year filter ${y}`),
    dataUploaded: false,
    setDataUploaded: () => {},
    attributionFilterOptions: ["noFilter", "outstanding"],
    attributionFilter: "noFilter",
    setAttributionFilter: (o: AttributionFilterOption) => console.log(`attributionFilter${0}`),
};

type DataContextType = {
    showExampleData: () => void;
    data: null | Data;
    rawDataReceived: (data: null | RawData) => void;
    yearsFilterOptions: number[];
    yearsFilter: number[];
    setYearsFilter: (year: number[]) => void;
    attributionFilterOptions: string[];
    attributionFilter: string;
    setAttributionFilter: (attribution: AttributionFilterOption) => void;
    dataUploaded: boolean;
    setDataUploaded: (dataUploaded: boolean) => void;
};

export const DataContext = createContext<DataContextType>(exampleContext);

export const DataProvider = ({ children }: PropsWithChildren) => {
    // stores incomming json parsed excelSheet data
    const [objectData, setObjectData] = useState<null | ObjectData>(null);

    // data used by widgets, must be provided in the right formatting
    const [data, setData] = useState<null | Data>(null);

    const [yearsFilter, setYearsFilter] = useState<number[]>([]);
    const [yearsFilterOptions, setYearsFilterOptions] = useState<number[]>([]);
    const [attributionFilter, setAttributionFilter] = useState<AttributionFilterOption>("noFilter");
    const [dataUploaded, setDataUploaded] = useState(false);

    const showExampleData = () => {
        setData(exampleData);
    };

    const rawDataReceived = (rawData: RawData | null) => {
        // make objects
        const newObjects = createObjectData(rawData);
        setObjectData(newObjects);
        // set filter options and value based on years in data
        const uniqueYears = Array.from(new Set(newObjects?.general.map((obj) => obj.year))).filter((num) => num !== undefined) as number[];
        setYearsFilterOptions(uniqueYears);
        setYearsFilter(uniqueYears);
        setAttributionFilter("noFilter");
        // useEffect will apply the filters and set data
    };

    useEffect(() => {
        const filter = {
            years: yearsFilter,
            attribution: attributionFilter,
        };
        const newData = !!objectData ? createTableAndChartData(objectData, filter) : null;
        setData(newData);
    }, [attributionFilter, objectData, yearsFilter]);

    const newValue = {
        data,
        rawDataReceived,
        showExampleData,
        yearsFilterOptions: yearsFilterOptions,
        yearsFilter,
        setYearsFilter,
        attributionFilterOptions: exampleContext.attributionFilterOptions,
        attributionFilter,
        setAttributionFilter,
        dataUploaded,
        setDataUploaded,
    };

    return <DataContext.Provider value={newValue}>{children}</DataContext.Provider>;
};
